// src/pages/VibeHomePage.jsx - Complete Vibe Edition Home Page with Life Design
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, Gamepad2, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';
import VibeHero from '../components/home/VibeHero';
import VibeModulesPreview from '../components/home/VibeModulesPreview';
import VibeStatsSection from '../components/home/VibeStatsSection';
import VibeCTASection from '../components/home/VibeCTASection';
import VibeFeaturesSection from '../components/home/VibeFeaturesSection';
import VibeToolsPreview from '../components/home/VibeToolsPreview';
import VibeLifeDesignHero from '../components/home/VibeLifeDesignHero';
import ModuleImageCarousel from '../components/shared/ModuleImageCarousel';

// Here's How It Works Section
const HowItWorksSection = () => (
  <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800">
    <div className="container mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto"
      >
        <div className="relative p-8 md:p-12 bg-slate-800/50 backdrop-blur-xl rounded-3xl border border-slate-700">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl" />
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">Here's the deal</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Your Self-Talk Upgrade Kit
            </h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Inner Voice Vibe is your interactive guide to levelling up how you talk to yourself. Take a quick assessment to discover your self-talk patterns, work through 8 modules that break down everything from thought fact-checking to emotion surfing, then use practical tools to shift your inner dialogue in real-time. No therapy couch needed—just you, your brain, and some actually useful techniques.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                <Brain className="w-8 h-8 text-purple-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">No Therapist Needed</h3>
                <p className="text-sm text-slate-400">Learn and level up on your own schedule—no appointments, no awkward sessions</p>
              </div>
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                <Gamepad2 className="w-8 h-8 text-pink-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Actually Works</h3>
                <p className="text-sm text-slate-400">Built on techniques that psychologists actually use, not just trendy self-help vibes</p>
              </div>
              <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-700">
                <Target className="w-8 h-8 text-indigo-400 mb-3" />
                <h3 className="text-white font-semibold mb-2">Use It Right Now</h3>
                <p className="text-sm text-slate-400">Tools you can literally use today when your brain starts spiralling</p>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-700">
              <p className="text-sm text-slate-400 italic">
                Part of the Dive Deep Dialogues mission: deeper dialogues build resilience
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const VibeHomePage = () => {
  const { setCurrentView } = useApp();
  const { user } = usePrivacyAuth();

  // ADD THIS MODULE DATA:
  const modulesData = [
    {
      id: 1,
      title: 'Understanding Your Inner Voice',
      subtitle: 'The DJ in Your Head That Never Stops Talking',
      description: 'Learn why your brain has a constant internal monologue and how to start taking control of it.',
      level: 'Foundation',
      series: 'Foundation',
      duration: '8 minutes',
      xpReward: 305,
      coolFactor: 'Like Spotify for your thoughts',
      image: '/images/tess-mod01-720px.png',
      imageAlt: 'DJ Tess with headphones mixing music, representing taking control of your inner voice',
    },
      {
    id: 2,
    title: 'Thought Detective Kit',
    subtitle: 'Fact-check your brain like a pro',
    description: 'Your brain loves creating fake news about your life. Time to become a detective and separate facts from fiction.',
    level: 'Foundation', 
    Series: 'Foundation',
        coolFactor: 'CSI but for your thoughts',
    duration: '10 min',
        xpReward: 100,
    image: '/images/tess-mod02-720px.png',
    imageAlt: 'Tess the detective with magnifying glass investigating thoughts',

  },
    {
      id: 3,
      title: 'Emotion Surfing Academy', 
      subtitle: 'Ride the waves instead of drowning',
      description: 'Emotions are like ocean waves - fight them and get wiped out, or learn to surf them like a pro.',
      level: 'Essential',
      series: 'Foundation',
      duration: '30-35 minutes',
      xpReward: 350,
      coolFactor: 'Emotional intelligence meets extreme sports', 
      image: '/images/tess-mod03-720px.png',
      imageAlt: 'Teen surfing emotional waves with confidence',
    },
    {
    id: 4,
    title: 'Social Confidence Arcade',
    subtitle: 'Level up your people skills',
    description: "Build genuine confidence that doesn't depend on everyone liking you. Practice authenticity and become magnetic.",
    level: 'Essential',
        coolFactor: 'Like a dating sim but for real life confidence',
    series: 'Foundation',
    xpReward: 125,
    duration: '15 min',
    image: '/images/tess-mod04-720px.png',
    imageAlt: 'Tess presenting with confidence',
  },
  {
    id: 5,
    title: 'Future Self Architect',
    subtitle: 'Design your life like your favourite character',
    description: "Stop living on autopilot. Design your future self like you're creating your favourite character in your favourite game.",
    level: 'Advanced',
        coolFactor: 'Character creation but for your actual life',
    series: 'Foundation',
    xpReward: 150,
    duration: '18 min',
    image: '/images/tess-mod05-720px.png',
    imageAlt: 'Twenty-something architect Tess in front of her new project',
  },
  {
    id: 6,
    title: 'Values & Identity Fortress',
    subtitle: 'Build your unshakeable foundation',
    description: "Discover what you actually stand for and create an identity that can't be shaken by other people's opinions.",
    level: 'Advanced',
        coolFactor: 'Like building your personal superhero origin story',
    series: 'Foundation',
    xpReward: 150,
    duration: '20 min',
    image: '/images/tess-mod06-720px.png',
    imageAlt: 'Knight of the Realm Tess holding up her sword in front of her castle',

  },
    {
    id: 7,
    title: 'Phoenix Rising Simulator',
    subtitle: 'Turn setbacks into comebacks',
    description: 'Learn to bounce back from anything life throws at you. Not just surviving tough times, but coming back stronger.',
    level: 'Mastery',
    category: 'Antifragility',
        coolFactor: 'Like a phoenix, but cooler',
    series: 'Foundation',
    xpReward: 175,
    duration: '22 min',
    image: '/images/tess-mod07-720px.png',
    imageAlt: 'Tess as a Phoenix rising',
    },
  {
    id: 8,
    title: 'Vibe Master Integration',
    subtitle: 'Become the main character of your life',
    description: "Put it all together and step into your power. You're not just surviving your thoughts - you're the director of your inner world.",
    level: 'Mastery',
        coolFactor: 'Final boss energy - you vs your old patterns',
    series: 'Foundation',
    xpReward: 200,
    duration: '25 min',
    image: '/images/tess-mod08-720px.png',
    imageAlt: 'Tess as a future leader taking control',
  },
    // Add more modules as you create their images...
  ];

  // ADD THIS HANDLER:
  const handleModuleClick = (module) => {
    setCurrentView('modules');
    // Could store the selected module ID for auto-opening later
  };
  
  // Handle Life Design tile click - route based on auth status
  const handleLifeDesignExplore = () => {
    if (user) {
      setCurrentView('life-design');
    } else {
      setCurrentView('life-design-landing');
    }
  };

  return (
<div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <VibeHero />

      {/* Features Overview */}
      <VibeFeaturesSection />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Featured Modules Carousel */}
<ModuleImageCarousel 
  modules={modulesData}
  onModuleClick={handleModuleClick}
  autoPlay={true}
  autoPlayInterval={7000}
/>
      
      {/* Modules Preview */}
      <VibeModulesPreview />
      
      {/* Interactive Tools Preview */}
      <VibeToolsPreview />
      
      {/* Life Design Promotional Tile - Strategic placement after tools */}
      <VibeLifeDesignHero onExplore={handleLifeDesignExplore} />
      
      
      {/* Final CTA */}
      <VibeCTASection />
    </div>
  );
};

export default VibeHomePage;